// Radiology report templates + patient reports — the ONLY module in the
// radiology feature that talks to storage. Everything above it (hooks,
// components) deals in plain objects and never knows where they came from.
//
// ── Backend integration ───────────────────────────────────────────────────
// The endpoints below don't exist yet. Until they do, TEMPLATES_LIVE /
// REPORTS_LIVE stay false and calls resolve against a localStorage adapter
// (namespaced by ORG-CODE) so the whole flow is usable end-to-end.
// To go live: flip the flag(s) to true and adjust ENDPOINTS/normalisers if the
// backend field names differ. No component changes are needed.
//
// Header convention follows ipd/api/dischargeProcessApi.js — Bearer token plus
// ORG-CODE / APP-MODE / USER-ACCESS-KEY / USERNAME. The org is resolved from
// those headers server-side; never send tenant or hospital ids from the client.
//
// ── Data contracts ────────────────────────────────────────────────────────
// Template {
//   template_id, template_name, modality, service_names[], description,
//   content_html,          // free-form HTML authored in the rich text editor
//   is_active, updated_at, updated_by
// }
// Report {
//   order_no, admission_id, template_id, template_name,   // provenance only
//   content_html,          // SNAPSHOT — never re-read from the template
//   status: "draft" | "final", radiologist_name, reported_at, updated_at
// }
//
// The frontend never parses content_html. A template is an opaque document:
// headings, tables, lists, placeholders — whatever the hospital authored.
import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

// Flip to true per resource as the backend ships it.
const TEMPLATES_LIVE = false;
const REPORTS_LIVE = true;

const ENDPOINTS = {
  templateList: "radiology/report-templates/",
  templateCreate: "radiology/report-templates/",
  templateUpdate: (id) => `radiology/report-templates/${id}/`,
  templateDelete: (id) => `radiology/report-templates/${id}/`,
  reportByOrder: (orderNo) => `radiology/reports/${orderNo}/`,
  reportSave: "radiology/reports/",
};

// ── Service-scoped templates (LIVE) ──────────────────────────────────────────
// These three exist on the backend today, unlike the CRUD set above. They model
// a template differently: it belongs to exactly ONE medical_service_id, and its
// body is stored as an uploaded HTML file rather than a JSON field. So the list
// call returns metadata only — content_html arrives later, from the file call.
// That laziness is why callers must fetch the file before applying a template.
const SERVICE_TEMPLATE_ENDPOINTS = {
  save: "save_radiology_report_template/",
  listByService: "get_radiology_report_templates/",
  file: "get_radiology_report_template_file/",
};

export const TEMPLATE_FILE_NAME = "radiology_template.html";

// save_radiology_report_template/ stores a file against a medical_service_id and
// nothing else — there is no name field — and get_radiology_report_templates/
// echoes back only `file_name`. So the template's name has to ride in the
// filename, or every entry in a radiologist's dropdown reads "Untitled template".
export function templateFileName(templateName) {
  const safe = String(templateName || "")
    .normalize("NFKD")
    // Non-ASCII (em dashes, accents) survives multipart inconsistently, and
    // these characters are illegal in filenames on the storage side.
    .replace(/[^\x20-\x7E]/g, "-")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
  return safe ? `${safe}.html` : TEMPLATE_FILE_NAME;
}

// `file_name` → display name. Templates uploaded before the name was carried in
// the filename all share the generic default, which names nothing — treat that
// as "no name" so the caller falls back rather than showing the placeholder.
const templateNameFromFile = (fileName) => {
  const base = String(fileName || "").replace(/\.html?$/i, "").trim();
  return base === "radiology_template" ? "" : base;
};

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

// Backends here return 200 with {status:"Failed"} for business errors, so a
// resolved promise is not proof of success — check the envelope too.
const unwrap = (res, listKeys = []) => {
  const data = res?.data;
  if (data?.status === "Failed") {
    throw new Error(data?.message || data?.error || "Request failed");
  }
  for (const key of listKeys) {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data?.[key])) return data.data[key];
  }
  return data?.data ?? data;
};

// Tolerant of naming drift between backend versions — one place to adjust.
// `content_html` is empty for rows that came from get_radiology_report_templates/
// — that endpoint returns metadata only, and the body has to be pulled with
// fetchTemplateFileHtml. An empty string here means "not loaded yet", not "empty
// template".
export const normalizeTemplate = (raw = {}) => ({
  template_id: String(raw.template_id ?? raw.id ?? raw.templateId ?? ""),
  // Falls back through: an explicit name → the name carried in the uploaded
  // filename → a dated placeholder, so two nameless uploads stay distinguishable
  // in the dropdown instead of both reading "Untitled template".
  template_name:
    raw.template_name ||
    raw.name ||
    raw.title ||
    templateNameFromFile(raw.file_name) ||
    (raw.uploaded_on
      ? `Untitled template (${String(raw.uploaded_on).slice(0, 10)})`
      : "Untitled template"),
  file_name: raw.file_name ?? "",
  uploaded_on: raw.uploaded_on ?? null,
  modality: raw.modality ?? raw.modality_name ?? "",
  medical_service_id: String(raw.medical_service_id ?? raw.medicalServiceId ?? ""),
  service_names: raw.service_names ?? raw.services ?? [],
  // Investigation chips with their service ids, so re-editing a template keeps
  // the id that scopes its upload. Falls back to id-less entries for templates
  // authored before ids were captured.
  service_refs: Array.isArray(raw.service_refs)
    ? raw.service_refs
    : (raw.service_names ?? raw.services ?? []).map((name) => ({
        name: String(name),
        medical_service_id: "",
      })),
  description: raw.description ?? raw.template_description ?? "",
  content_html: raw.content_html ?? raw.template_html ?? raw.content ?? raw.html_content ?? "",
  is_active: raw.is_active !== undefined ? !!raw.is_active : true,
  updated_at: raw.updated_at ?? raw.modified_at ?? raw.uploaded_on ?? null,
  updated_by: raw.updated_by ?? raw.modified_by ?? "",
});

export const normalizeReport = (raw = {}) => ({
  order_no: String(raw.order_no ?? raw.orderNo ?? ""),
  admission_id: raw.admission_id ?? raw.admissionId ?? null,
  template_id: raw.template_id ? String(raw.template_id) : null,
  template_name: raw.template_name ?? "",
  content_html: raw.content_html ?? raw.report_html ?? raw.content ?? "",
  status: raw.status === "final" || raw.finalized ? "final" : "draft",
  radiologist_name: raw.radiologist_name ?? raw.radiologistName ?? "",
  reported_at: raw.reported_at ?? raw.reportedAt ?? null,
  updated_at: raw.updated_at ?? null,
});

/* ══════════════════════════════════════════════════════════════════════
   localStorage adapter — the stand-in until the endpoints above exist.
   Delete this block once both *_LIVE flags are true.
   ══════════════════════════════════════════════════════════════════════ */
const lsKey = (what) =>
  `htmate.radiology.${what}.v2.${sessionStorage.getItem("ORG_CODE") || "default"}`;

const templateServicesKey = () => lsKey("templateServices");

/** Investigations that have at least one uploaded template — used to reload the catalog. */
export function readTemplateServiceRegistry() {
  try {
    const raw = window.localStorage.getItem(templateServicesKey());
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

export function registerTemplateService({
  medical_service_id,
  medical_service_no,
  name,
  id_aliases,
} = {}) {
  const id = String(medical_service_id || "").trim();
  if (!id) return;
  const aliases = [
    ...new Set(
      [id, medical_service_no, ...(id_aliases || [])]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean),
    ),
  ];
  const prev = readTemplateServiceRegistry();
  const next = [
    {
      medical_service_id: id,
      medical_service_no: String(medical_service_no || "").trim(),
      name: String(name || "").trim(),
      id_aliases: aliases,
    },
    ...prev.filter((row) => {
      const prevKeys = new Set(
        [row.medical_service_id, row.medical_service_no, ...(row.id_aliases || [])]
          .map((v) => String(v ?? "").trim())
          .filter(Boolean),
      );
      return !aliases.some((alias) => prevKeys.has(alias));
    }),
  ].slice(0, 50);
  try {
    window.localStorage.setItem(templateServicesKey(), JSON.stringify(next));
  } catch (e) {
    /* quota — catalog reload may miss this service until next save */
  }
}

/** Backend save creates a new S3 object unless template_id is sent — track replacements. */
export function markTemplateSuperseded(oldId, newId) {
  const prev = String(oldId || "").trim();
  const next = String(newId || "").trim();
  if (!prev || !next || prev === next) return;
  const map = lsRead("templateSuperseded", {});
  map[prev] = next;
  lsWrite("templateSuperseded", map);
}

function filterSupersededTemplates(list) {
  const map = lsRead("templateSuperseded", {});
  const hidden = new Set(Object.keys(map || {}));
  return (list || []).filter((row) => !hidden.has(String(row.template_id)));
}

/** Name, modality, active flag — not stored server-side beyond the filename. */
export function writeTemplateMeta(templateId, meta = {}) {
  const id = String(templateId || "").trim();
  if (!id) return;
  const store = lsRead("templateMeta", {});
  store[id] = { ...store[id], ...meta, template_id: id };
  lsWrite("templateMeta", store);
}

function applyTemplateMeta(list) {
  const store = lsRead("templateMeta", {});
  return (list || []).map((row) => {
    const meta = store[String(row.template_id)];
    if (!meta) return row;
    return {
      ...row,
      template_name: meta.template_name || row.template_name,
      modality: meta.modality ?? row.modality,
      description: meta.description ?? row.description,
      is_active: meta.is_active !== undefined ? meta.is_active : row.is_active,
      service_names: meta.service_names?.length ? meta.service_names : row.service_names,
    };
  });
}

function finalizeTemplateList(list) {
  return applyTemplateMeta(filterSupersededTemplates(list)).sort((a, b) =>
    String(b.uploaded_on || "").localeCompare(String(a.uploaded_on || "")),
  );
}

const lsRead = (what, fallback) => {
  try {
    const raw = window.localStorage.getItem(lsKey(what));
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const lsWrite = (what, value) => {
  try {
    window.localStorage.setItem(lsKey(what), JSON.stringify(value));
  } catch (e) {
    // Quota or private-mode failure — surface as a rejected save.
    throw new Error("Could not save locally. Storage is full or unavailable.");
  }
};

const localId = () => `tpl_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const nowIso = () => new Date().toISOString();

/* ══════════════════════════════════════════════════════════════════════
   Templates — CRUD
   ══════════════════════════════════════════════════════════════════════ */

export async function fetchTemplates() {
  if (!TEMPLATES_LIVE) {
    return lsRead("templates", []).map(normalizeTemplate);
  }
  const res = await axios.get(`${BASE}${ENDPOINTS.templateList}`, {
    headers: authHeaders(),
  });
  const list = unwrap(res, ["templates", "results", "data"]);
  return (Array.isArray(list) ? list : []).map(normalizeTemplate);
}

export async function createTemplate(template) {
  const body = {
    template_name: template.template_name,
    modality: template.modality,
    // Kept alongside the names so re-opening a template for edit still knows
    // which investigation it was uploaded against.
    medical_service_id: template.medical_service_id || "",
    service_names: template.service_names || [],
    service_refs: template.service_refs || [],
    description: template.description || "",
    content_html: template.content_html || "",
    is_active: template.is_active !== false,
  };

  if (!TEMPLATES_LIVE) {
    const saved = normalizeTemplate({
      ...body,
      template_id: localId(),
      updated_at: nowIso(),
      updated_by: sessionStorage.getItem("userName") || "",
    });
    lsWrite("templates", [...lsRead("templates", []), saved]);
    return saved;
  }

  const res = await axios.post(`${BASE}${ENDPOINTS.templateCreate}`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return normalizeTemplate(unwrap(res));
}

export async function updateTemplate(templateId, template) {
  const body = {
    template_name: template.template_name,
    modality: template.modality,
    // Kept alongside the names so re-opening a template for edit still knows
    // which investigation it was uploaded against.
    medical_service_id: template.medical_service_id || "",
    service_names: template.service_names || [],
    service_refs: template.service_refs || [],
    description: template.description || "",
    content_html: template.content_html || "",
    is_active: template.is_active !== false,
  };

  if (!TEMPLATES_LIVE) {
    const saved = normalizeTemplate({
      ...body,
      template_id: templateId,
      updated_at: nowIso(),
      updated_by: sessionStorage.getItem("userName") || "",
    });
    lsWrite(
      "templates",
      lsRead("templates", []).map((t) =>
        String(t.template_id) === String(templateId) ? saved : t,
      ),
    );
    return saved;
  }

  const res = await axios.put(`${BASE}${ENDPOINTS.templateUpdate(templateId)}`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return normalizeTemplate(unwrap(res));
}

export async function deleteTemplate(templateId) {
  if (!TEMPLATES_LIVE) {
    lsWrite(
      "templates",
      lsRead("templates", []).filter((t) => String(t.template_id) !== String(templateId)),
    );
    return true;
  }
  const res = await axios.delete(`${BASE}${ENDPOINTS.templateDelete(templateId)}`, {
    headers: authHeaders(),
  });
  unwrap(res);
  return true;
}

/* ══════════════════════════════════════════════════════════════════════
   Templates — service-scoped, file-backed (LIVE endpoints)
   ══════════════════════════════════════════════════════════════════════ */

/** HTML string → File, the shape save_radiology_report_template/ expects. */
export function htmlToTemplateFile(html, fileName = TEMPLATE_FILE_NAME) {
  return new File([html ?? ""], fileName, { type: "text/html" });
}

/**
 * Base64 → HTML.
 *
 * atob() returns a *binary string* — one character per byte — so any multi-byte
 * UTF-8 in the report (°, ±, μ, ≥, patient names in Indic scripts) comes back
 * as mojibake if used directly. Decode the bytes as UTF-8 instead, falling back
 * to the raw string only where TextDecoder is unavailable.
 */
export function decodeBase64Html(base64) {
  const raw = String(base64 || "")
    // Some gateways hand back a full data URL rather than bare base64.
    .replace(/^data:[^;]*;base64,/i, "")
    .trim();
  if (!raw) return "";
  let binary;
  try {
    binary = atob(raw);
  } catch (e) {
    throw new Error("The template file could not be decoded.");
  }
  try {
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch (e) {
    return binary;
  }
}

/**
 * API 1 — POST save_radiology_report_template/ (multipart/form-data)
 * Fields: medical_service_id, template_file
 *
 * `templateName` becomes the uploaded file's name — the only place the backend
 * keeps it (see templateFileName).
 */
export async function saveRadiologyReportTemplate({
  medicalServiceId,
  contentHtml,
  templateName = "",
  fileName,
  templateId,
}) {
  if (!medicalServiceId) {
    throw new Error("No investigation selected — a template must belong to one.");
  }
  const form = new FormData();
  form.append("medical_service_id", String(medicalServiceId));
  const editingId = String(templateId || "").trim();
  if (editingId) {
    form.append("template_id", editingId);
  }
  form.append(
    "template_file",
    htmlToTemplateFile(contentHtml, fileName || templateFileName(templateName)),
  );

  // Let axios set multipart boundary — a manual Content-Type breaks uploads.
  const res = await axios.post(`${BASE}${SERVICE_TEMPLATE_ENDPOINTS.save}`, form, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

/**
 * API 2 — POST get_radiology_report_templates/  { medical_service_id }
 * Every template authored for one investigation. Metadata only — see
 * normalizeTemplate's note on content_html.
 */
const normServiceLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Every medical_service_id shape an order or saved template may use. */
export function collectTemplateServiceIdCandidates(order) {
  const out = [];
  const add = (value) => {
    const id = String(value ?? "").trim();
    if (!id || id === "-" || out.includes(id)) return;
    out.push(id);
  };
  if (!order || typeof order !== "object") return out;

  add(order.medical_service_uuid);
  add(order.medicalServiceId);
  add(order.medical_service_id);
  add(order.service_id);
  add(order.service_uuid);
  add(order.medicalServiceNo);
  add(order.medical_service_no);

  const orderService = normServiceLabel(order.service);
  readTemplateServiceRegistry().forEach((row) => {
    const rowService = normServiceLabel(row.name);
    const nameMatches =
      orderService &&
      rowService &&
      (rowService === orderService ||
        rowService.includes(orderService) ||
        orderService.includes(rowService));
    if (!nameMatches) return;
    add(row.medical_service_id);
    add(row.medical_service_no);
    (row.id_aliases || []).forEach(add);
  });

  return out;
}

/**
 * Radiology orders often carry the numeric service pk while templates were
 * uploaded against the charge-capture UUID — try every known id for the study.
 */
function mergeTemplatesInto(target, list) {
  (list || []).forEach((row) => {
    if (row?.template_id) target.set(row.template_id, row);
  });
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TEMPLATE_LIST_CACHE_MS = 15000;
const templatesByServiceInflight = new Map();
const templatesByServiceCache = new Map();

function dedupeInFlight(store, key, factory) {
  if (store.has(key)) return store.get(key);
  const promise = Promise.resolve()
    .then(factory)
    .finally(() => store.delete(key));
  store.set(key, promise);
  return promise;
}

function readServiceTemplateCache(key) {
  const hit = templatesByServiceCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > TEMPLATE_LIST_CACHE_MS) {
    templatesByServiceCache.delete(key);
    return null;
  }
  return hit.data;
}

/** Clear cached template lists — call before an explicit reload. */
export function clearTemplateListCache(serviceId) {
  if (serviceId != null && String(serviceId).trim()) {
    templatesByServiceCache.delete(String(serviceId).trim());
    return;
  }
  templatesByServiceCache.clear();
}

function pickPrimaryServiceId(order, candidates = []) {
  if (order?.medicalServiceId) return String(order.medicalServiceId).trim();
  const uuid = [
    order?.medical_service_uuid,
    order?.medical_service_id,
    order?.service_id,
    order?.service_uuid,
  ].find((v) => UUID_RE.test(String(v ?? "")));
  if (uuid) return String(uuid);
  if (order?.medical_service_id) return String(order.medical_service_id).trim();
  return candidates[0] || "";
}

export async function fetchTemplatesForRadiologyOrder(order) {
  const candidates = collectTemplateServiceIdCandidates(order);
  const merged = new Map();
  const tried = new Set();

  const tryId = async (id) => {
    const key = String(id ?? "").trim();
    if (!key || key === "-" || tried.has(key)) return;
    tried.add(key);
    try {
      mergeTemplatesInto(merged, await fetchTemplatesForService(key));
    } catch (e) {
      /* try remaining candidate ids */
    }
  };

  const primary = pickPrimaryServiceId(order, candidates);
  if (primary) await tryId(primary);
  if (merged.size > 0) {
    return finalizeTemplateList([...merged.values()]);
  }

  for (const id of candidates) {
    if (merged.size > 0) break;
    await tryId(id);
  }
  if (merged.size > 0) {
    return finalizeTemplateList([...merged.values()]);
  }

  const orderService = normServiceLabel(order?.service);
  if (orderService) {
    for (const row of readTemplateServiceRegistry()) {
      if (merged.size > 0) break;
      const rowService = normServiceLabel(row.name);
      if (!rowService) continue;
      const nameMatches =
        rowService === orderService ||
        rowService.includes(orderService) ||
        orderService.includes(rowService);
      if (!nameMatches) continue;
      await tryId(row.medical_service_id);
      for (const alias of row.id_aliases || []) await tryId(alias);
    }
  }

  return finalizeTemplateList([...merged.values()]);
}

async function fetchTemplatesForServiceUncached(medicalServiceId) {
  const res = await axios.post(
    `${BASE}${SERVICE_TEMPLATE_ENDPOINTS.listByService}`,
    { medical_service_id: String(medicalServiceId) },
    { headers: authHeaders({ "Content-Type": "application/json" }) },
  );
  const data = res?.data;
  if (data?.status === "Failed") {
    throw new Error(data?.message || data?.error || "Request failed");
  }
  const list = Array.isArray(data?.templates) ? data.templates : [];
  const serviceName = String(data?.service_name || "").trim();
  const svcId = String(data?.medical_service_id || medicalServiceId);
  return finalizeTemplateList(
    list.map((row) =>
      normalizeTemplate({
        ...row,
        medical_service_id: svcId,
        service_names: serviceName ? [serviceName] : [],
        service_refs: serviceName
          ? [{ name: serviceName, medical_service_id: svcId }]
          : [{ name: "", medical_service_id: svcId }],
      }),
    ),
  );
}

export async function fetchTemplatesForService(medicalServiceId) {
  const id = String(medicalServiceId ?? "").trim();
  if (!id || id === "-") return [];

  const cached = readServiceTemplateCache(id);
  if (cached) return cached;

  return dedupeInFlight(templatesByServiceInflight, id, async () => {
    const list = await fetchTemplatesForServiceUncached(id);
    templatesByServiceCache.set(id, { at: Date.now(), data: list });
    return list;
  });
}

/** Load templates for every investigation this org has uploaded against. */
export async function fetchTemplatesCatalog() {
  const services = readTemplateServiceRegistry();
  if (!services.length) return [];
  const batches = await Promise.all(
    services.map(async (svc) => {
      try {
        return await fetchTemplatesForService(svc.medical_service_id);
      } catch (e) {
        return [];
      }
    }),
  );
  const byId = new Map();
  batches.flat().forEach((row) => {
    if (row.template_id) byId.set(row.template_id, row);
  });
  return finalizeTemplateList([...byId.values()]);
}

/**
 * API 3 — POST get_radiology_report_template_file/  { template_id, medical_service_id }
 * Returns { file_content: "<base64>" }; resolves to the decoded HTML.
 */
export async function fetchTemplateFileHtml({ templateId, medicalServiceId }) {
  if (!templateId) throw new Error("No template selected.");
  const res = await axios.post(
    `${BASE}${SERVICE_TEMPLATE_ENDPOINTS.file}`,
    {
      template_id: String(templateId),
      medical_service_id: String(medicalServiceId || ""),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) },
  );
  const data = unwrap(res);
  const encoded =
    data?.file_content ?? data?.template_file ?? data?.file ?? data?.content ?? "";
  return decodeBase64Html(encoded);
}

/* ══════════════════════════════════════════════════════════════════════
   Patient reports
   ══════════════════════════════════════════════════════════════════════ */

// One request per order for a few seconds: the report dialog and the report
// modal both ask for the same order as the modal opens. Cleared on save.
const REPORT_READ_CACHE_MS = 5000;
const reportReads = new Map();

export async function fetchReport(orderNo) {
  if (!orderNo) return null;
  if (!REPORTS_LIVE) {
    const found = lsRead("reports", {})[orderNo];
    return found ? normalizeReport(found) : null;
  }
  const hit = reportReads.get(orderNo);
  if (hit && Date.now() - hit.at < REPORT_READ_CACHE_MS) return hit.promise;
  const promise = (async () => {
    const res = await axios.get(`${BASE}${ENDPOINTS.reportByOrder(orderNo)}`, {
      headers: authHeaders(),
    });
    // No report saved yet — the endpoint answers {status:"Success", data:null}.
    if (res?.data?.data === null) return null;
    const data = unwrap(res);
    return data ? normalizeReport(data) : null;
  })();
  reportReads.set(orderNo, { at: Date.now(), promise });
  promise.catch(() => reportReads.delete(orderNo));
  return promise;
}

// content_html is the report's own copy of the text. The template it came from
// is recorded for traceability only — editing or deleting that template later
// must never alter a saved report.
export async function saveReport(report) {
  reportReads.delete(report.order_no);
  const body = {
    order_no: report.order_no,
    admission_id: report.admission_id ?? null,
    template_id: report.template_id ?? null,
    template_name: report.template_name ?? "",
    content_html: report.content_html ?? "",
    status: report.status === "final" ? "final" : "draft",
    radiologist_name: report.radiologist_name ?? "",
    reported_at: report.status === "final" ? report.reported_at || nowIso() : null,
  };

  if (!REPORTS_LIVE) {
    const saved = normalizeReport({ ...body, updated_at: nowIso() });
    lsWrite("reports", { ...lsRead("reports", {}), [saved.order_no]: saved });
    return saved;
  }

  const res = await axios.post(`${BASE}${ENDPOINTS.reportSave}`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return normalizeReport(unwrap(res));
}

export const isTemplatesApiLive = () => TEMPLATES_LIVE;
export const isReportsApiLive = () => REPORTS_LIVE;
